import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Container, Kicker, SectionHeading } from "../components/primitives";
import { MarketCard } from "../components/MarketCard";
import { ChangePill } from "../components/ChangePill";
import { TrendBadge } from "../components/TrendBadge";
import { MoodGauge } from "../components/MoodGauge";
import { AdSlot } from "../components/AdSlot";
import { Heatmap } from "../components/Heatmap";
import { DataMeta } from "../components/DataMeta";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useMarkets, useMovers, useMood } from "../hooks/useMarket";
import { HOME_TICKERS } from "../lib/assets";
import { formatPrice, formatPercent, changeColor } from "../lib/format";
import { WHATS_MOVING } from "../lib/content";
import { pathFor } from "../lib/assets";

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.18] dark:opacity-25"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1751738567801-7d13e5b51b1d)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(to bottom, black, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
        }}
      />
      <Container className="relative py-20 sm:py-28">
        <div className="max-w-3xl animate-fade-up">
          <Kicker>{t("home.heroKicker")}</Kicker>
          <h1 className="mt-4 text-4xl font-normal leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {t("home.heroTitle")}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("home.heroSubtitle")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" data-testid="hero-cta-markets">
              <Link to="/markets">{t("home.ctaExplore")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="hero-cta-tools">
              <Link to="/tools">{t("home.ctaTools")}</Link>
            </Button>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {t("common.notAdviceShort")}
          </div>
        </div>
      </Container>
    </section>
  );
}

function MarketsToday() {
  const { t } = useTranslation();
  const { data, isLoading } = useMarkets();
  const bySymbol = Object.fromEntries((data?.markets || []).map((m) => [m.symbol, m]));
  const cards = HOME_TICKERS.map((s) => bySymbol[s]).filter(Boolean);

  return (
    <section className="py-16 sm:py-20">
      <Container>
        <SectionHeading
          kicker={t("home.marketsToday")}
          title={t("home.marketsToday")}
          sub={t("home.marketsTodaySub")}
          action={<DataMeta meta={data?.meta} className="shrink-0" />}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-[132px] rounded-lg" />)
            : cards.map((a) => <MarketCard key={a.symbol} asset={a} />)}
        </div>
      </Container>
    </section>
  );
}

function MarketOverview() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useMarkets();
  const rows = data?.markets || [];
  const categoryLabel = (category) => category === "metal"
    ? t("common.metals")
    : category === "crypto"
      ? t("common.cryptocurrencies")
      : t(`nav.${category}`, { defaultValue: category });

  return (
    <section className="py-16 sm:py-20">
      <Container>
        <SectionHeading kicker={t("common.overview")} title={t("home.marketOverview")} sub={t("home.marketOverviewSub")} />
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="px-5 py-3 text-start font-medium">{t("common.category")}</th>
                <th className="px-5 py-3 text-end font-medium">{t("common.price")}</th>
                <th className="hidden px-5 py-3 text-end font-medium sm:table-cell">{t("common.change24h")}</th>
                <th className="hidden px-5 py-3 text-end font-medium md:table-cell">{t("common.change7d")}</th>
                <th className="px-5 py-3 text-end font-medium">{t("common.trend")}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-border last:border-0"><td colSpan={5} className="px-5 py-4"><Skeleton className="h-5 w-full" /></td></tr>
                  ))
                : rows.map((a) => (
                    <tr key={a.symbol} className="border-b border-border transition-colors last:border-0 hover:bg-accent/50">
                      <td className="px-5 py-3.5">
                        <Link to={pathFor(a.symbol)} className="flex flex-col" data-testid={`overview-row-${a.symbol}`}>
                          <span className="font-medium text-foreground">{t(`nav.${a.symbol}`, { defaultValue: a.name })}</span>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{categoryLabel(a.category)}</span>
                        </Link>
                      </td>
                      <td className="num px-5 py-3.5 text-end font-medium text-foreground">{formatPrice(a.price, a.quoteCurrency, i18n.language)}</td>
                      <td className={`num hidden px-5 py-3.5 text-end sm:table-cell ${changeColor(a.change24h)}`}>{formatPercent(a.change24h, i18n.language)}</td>
                      <td className={`num hidden px-5 py-3.5 text-end md:table-cell ${changeColor(a.change7d)}`}>{formatPercent(a.change7d, i18n.language)}</td>
                      <td className="px-5 py-3.5 text-end"><TrendBadge trend={a.trend} /></td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  );
}

function MoverList({ title, items }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</h3>
      <div className="divide-y divide-border">
        {items.map((a) => (
          <Link key={a.symbol} to={pathFor(a.symbol)} className="flex items-center justify-between py-3 transition-colors hover:text-primary" data-testid={`mover-${a.symbol}`}>
            <span className="font-medium">{t(`nav.${a.symbol}`, { defaultValue: a.name })}</span>
            <div className="flex items-center gap-4">
              <span className="num text-sm text-muted-foreground">{formatPrice(a.price, a.quoteCurrency, i18n.language)}</span>
              <ChangePill value={a.change24h} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function TopMovers() {
  const { t } = useTranslation();
  const { data, isLoading } = useMovers();
  if (isLoading) return null;
  return (
    <section className="py-16 sm:py-20">
      <Container>
        <SectionHeading kicker={t("common.topMovers")} title={t("home.topMovers")} sub={t("home.topMoversSub")} />
        <div className="grid gap-4 md:grid-cols-2">
          <MoverList title={t("common.gainers")} items={data?.gainers || []} />
          <MoverList title={t("common.losers")} items={data?.losers || []} />
        </div>
      </Container>
    </section>
  );
}

function MarketMood() {
  const { t } = useTranslation();
  const { data, isLoading } = useMood();
  if (isLoading) return null;
  const catLabel = { metal: t("common.metals"), crypto: t("common.cryptocurrencies"), energy: t("nav.energy"), forex: t("nav.forex") };
  return (
    <section className="py-16 sm:py-20">
      <Container>
        <SectionHeading kicker={t("home.marketMood")} title={t("home.marketMood")} sub={t("home.marketMoodSub")} />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-6 lg:col-span-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t("home.overallMood")}</div>
            <div className="mt-3 font-heading text-5xl" style={{ color: data.overall.state === "bullish" ? "hsl(var(--bullish))" : data.overall.state === "bearish" ? "hsl(var(--bearish))" : "hsl(var(--neutral))" }}>
              {t(`common.${data.overall.state}`)}
            </div>
            <div className="num mt-2 text-sm text-muted-foreground">{t("common.momentum")} {t("localizedContent.ui.score", { defaultValue: "score" })}: {data.overall.score}/100</div>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t("mood.description")}</p>
            <Link to="/methodology#mood" className="mt-3 inline-block text-xs font-medium text-primary hover:underline" data-testid="mood-how-calculated">{t("x.howCalculated")}</Link>
          </div>
          <div className="grid gap-5 rounded-lg border border-border bg-card p-6 sm:grid-cols-2 lg:col-span-2">
            {(data.categories || []).map((c) => (
              <MoodGauge key={c.category} score={c.score} state={c.state} label={catLabel[c.category] || c.category} sublabel={`${c.bullishCount}/${c.total} ${t("common.bullish").toLowerCase()}`} />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function WhatsMoving() {
  const { t } = useTranslation();
  return (
    <section className="py-16 sm:py-20">
      <Container>
        <SectionHeading kicker={t("localizedContent.ui.context", { defaultValue: "Context" })} title={t("home.whatsMoving")} sub={t("home.whatsMovingSub")} />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {WHATS_MOVING.map((c) => (
            <div key={c.id} className="flex flex-col rounded-lg border border-border bg-card p-6" data-testid={`whats-moving-${c.id}`}>
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">{t(`localizedContent.whatsMoving.${c.id}.tag`, { defaultValue: c.tag })}</span>
              <h3 className="mt-3 text-lg leading-snug text-foreground">{t(`localizedContent.whatsMoving.${c.id}.title`, { defaultValue: c.title })}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t(`localizedContent.whatsMoving.${c.id}.body`, { defaultValue: c.body })}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function MarketOutlook() {
  const { t, i18n } = useTranslation();
  const { data } = useMarkets();
  const rows = (data?.markets || []).filter((m) => ["gold", "bitcoin", "brent", "eurusd", "ethereum", "silver"].includes(m.symbol));
  return (
    <section className="py-16 sm:py-20">
      <Container>
        <SectionHeading kicker={t("localizedContent.ui.indicators", { defaultValue: "Indicators" })} title={t("home.marketOutlook")} sub={t("home.marketOutlookSub")} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((a) => (
            <div key={a.symbol} className="rounded-lg border border-border bg-card p-6" data-testid={`outlook-${a.symbol}`}>
              <div className="flex items-center justify-between">
                <Link to={pathFor(a.symbol)} className="font-heading text-lg hover:text-primary">{t(`nav.${a.symbol}`, { defaultValue: a.name })}</Link>
                <TrendBadge trend={a.trend} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                {[
                  { l: t("common.change7d"), v: formatPercent(a.change7d, i18n.language), c: changeColor(a.change7d) },
                  { l: t("common.change30d"), v: formatPercent(a.change30d, i18n.language), c: changeColor(a.change30d) },
                  { l: t("common.volatility"), v: `${a.volatility}%`, c: "text-foreground" },
                ].map((x) => (
                  <div key={x.l}>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{x.l}</div>
                    <div className={`num mt-1 text-sm font-semibold ${x.c}`}>{x.v}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo path="/" jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "MARDATIQ", url: typeof window !== "undefined" ? window.location.origin : "" }} />
      <Hero />
      <MarketsToday />
      <Container><AdSlot format="leaderboard" /></Container>
      <MarketOverview />
      <Heatmap />
      <TopMovers />
      <MarketMood />
      <WhatsMoving />
      <MarketOutlook />
    </>
  );
}
